import React from "react";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { infoFor } from "../lib/paramInfo";

/**
 * Small <i> hover affordance that shows what a parameter means and why it
 * matters for athletes. Use anywhere a parameter is named — pills, snapshot
 * cards, spreadsheet headers, etc.
 *
 * Props:
 *   paramKey: the canonical key (e.g. "hb", "iron")
 *   low / high: optional ref-range limits to display inside the tooltip
 *   className: optional override for the trigger size
 *
 * NOTE: `low`/`high` are flat primitives (not a `range={{}}` object) so the
 * component can be memoized — passing an inline object would re-render the
 * tooltip every render of the parent.
 */
export default function ParamInfo({ paramKey, low = null, high = null, className = "" }) {
  const info = infoFor(paramKey);
  if (!info) return null;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            // Don't let clicks bubble up to a parent pill / row — opening the
            // tooltip should not also toggle a parameter chip.
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            aria-label={`About ${info.name}`}
            data-testid={`param-info-${paramKey}`}
            className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-slate-500 hover:text-cyan-300 transition-colors cursor-help ${className}`}
          >
            <Info size={12} strokeWidth={2.2} />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          sideOffset={6}
          className="max-w-xs shadow-xl rounded-lg px-3 py-2.5"
          style={{
            background: "#FCD34D",
            color: "#111827",
            border: "1px solid #B45309",
          }}
        >
          <div className="font-display text-sm mb-0.5" style={{ color: "#7C2D12" }}>{info.name}</div>
          <div className="text-[12px] leading-snug mb-1.5" style={{ color: "#111827" }}>{info.short}</div>
          <div className="text-[11px] leading-snug" style={{ color: "#1F2937" }}>
            <span className="uppercase tracking-wider" style={{ color: "#7C2D12" }}>Why it matters: </span>
            {info.why}
          </div>
          {(low != null || high != null) && (
            <div className="mt-2 pt-1.5 border-t text-[11px] font-mono-data" style={{ borderColor: "#B45309", color: "#1F2937" }}>
              {low != null && (
                <>
                  <span style={{ color: "#7C2D12" }}>Low</span> {low}
                </>
              )}
              {low != null && high != null && <span className="mx-2">→</span>}
              {high != null && (
                <>
                  <span style={{ color: "#7C2D12" }}>High</span> {high}
                </>
              )}
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
